/** @file Fills RenderBuffers from a pose: quads, meshes, deform, draw order, colors, batching. */
// Hot path: geometry/src has function-size, complexity and param limits off; keep files ≤200 lines.
import { POSE_LAYOUT } from '@pose2d/core/contracts/pose';
import type { AttachmentData, Pose, SkeletonData, SkinData } from '@pose2d/core/types';
import { invariant } from '@pose2d/std/invariant';
import { meshVertices } from './meshVertices';
import { regionQuad } from './regionQuad';
import { RENDER_LAYOUT, type RenderBuffers } from './renderBuffers';
import { skinVertices } from './skinVertices';
import { slotWorld } from './slotWorld';
import type { TexturePages } from './texturePages';
import { appendDraw } from './_internal/appendDraw';
import { createBuildState } from './_internal/buildState';
import { flushDraw } from './_internal/flushDraw';
import { positionScratch } from './_internal/positionScratch';
import { tintWord } from './_internal/tintWord';
import { writeRun } from './_internal/writeRun';
import { writeTriangles } from './_internal/writeTriangles';
import { writeVertices } from './_internal/writeVertices';

const VW = RENDER_LAYOUT.VERTEX_WORDS;
const WS = POSE_LAYOUT.WORLD_STRIDE;
/** Two triangles over the region corners BL, TL, TR, BR. */
const REGION_TRIANGLES = [0, 1, 2, 0, 2, 3] as const;
/** The one pass state, reset by every call: buildRender allocates nothing per frame. */
const st = createBuildState();
/** Scratch affine: the slot world of the region or rigid mesh being written (slotWorld, W2-21). */
const slotAffine = new Float64Array(WS);

/** Zero cursors and flags, empty bounds, no open draw. */
function reset(): void {
  st.v = 0; st.i = 0; st.d = 0; st.r = 0;
  st.changed = false; st.topo = false;
  st.minX = Infinity; st.minY = Infinity; st.maxX = -Infinity; st.maxY = -Infinity;
  st.draw[0] = -1;
}

/** World positions of `att` shown by `slot` into `pos`: skinned meshes from their bones, others through slotWorld. */
function positions(pos: Float32Array, pose: Pose, data: SkeletonData, slot: number, att: AttachmentData, deform: Float64Array | null): void {
  if (att.type === 'mesh' && att.skin.kind === 'skinned') {
    skinVertices(pos, 0, att, pose.world, deform);
    return;
  }
  slotWorld(pose, data, slot, slotAffine);
  if (att.type === 'region') regionQuad(pos, 0, att, slotAffine, 0);
  else meshVertices(pos, 0, att, slotAffine, 0, deform);
}

/**
 * Appends what `slot` shows (if anything): triangles, vertices, its draw range and, by slot, a run. An attachment
 * without vertices shows nothing (createRenderBuffers gives a slot that can only show such ones no draw or run row).
 */
function emit(out: RenderBuffers, pose: Pose, data: SkeletonData, slot: number, skeletonPages: number, bySlot: boolean): void {
  const slotData = data.slots[slot];
  const attIndex = pose.slotAttachment[slot] ?? -1;
  if (slotData === undefined || attIndex < 0) return;
  const att = data.attachments[attIndex];
  invariant(att !== undefined, 'buildRender: pose.slotAttachment names a missing attachment');
  invariant(att.page >= 0 && att.page < skeletonPages, 'buildRender: attachment page is not a skeleton page (TexturePages.white bounds them)');
  const count = att.type === 'region' ? 4 : att.uvs.length / 2;
  if (count === 0) return;
  const tris = att.type === 'region' ? REGION_TRIANGLES : att.triangles;
  invariant(
    st.v + count <= out.vertexF32.length / VW && st.i + tris.length <= out.indices.length,
    'buildRender: vertex/index capacity exceeded (size with createRenderBuffers for this SkeletonData or pass extras)',
  );
  const deform = pose.deform[attIndex];
  const pos = positionScratch(2 * count);
  positions(pos, pose, data, slot, att, deform !== undefined && deform.length > 0 ? deform : null);
  const v0 = st.v, i0 = st.i;
  writeTriangles(out, st, tris, v0);
  writeVertices(out, st, pos, att.uvs, count, tintWord(pose.slotColor[slot] ?? 0, att.color), (pose.slotDark[slot] ?? 0) & 0xffffff);
  appendDraw(out, st, att.page, slotData.blend, i0, tris.length, v0, count);
  if (bySlot) writeRun(out, st, slot, i0, tris.length);
}

/** Writes counts and bounds, then bumps version / topologyVersion when the pass changed anything. */
function finish(out: RenderBuffers): void {
  if (out.vertexCount !== st.v) st.changed = true;
  if (out.indexCount !== st.i || out.drawCount !== st.d || out.runCount !== st.r) {
    st.changed = true;
    st.topo = true;
  }
  out.vertexCount = st.v;
  out.indexCount = st.i;
  out.drawCount = st.d;
  out.runCount = st.r;
  const empty = st.v === 0;
  out.bounds[0] = empty ? 0 : st.minX;
  out.bounds[1] = empty ? 0 : st.minY;
  out.bounds[2] = empty ? 0 : st.maxX;
  out.bounds[3] = empty ? 0 : st.maxY;
  if (st.changed) out.version += 1;
  if (st.topo) out.topologyVersion += 1;
}

/**
 * Zero-allocation per frame; bumps version (and topologyVersion when indices/draws/runs change) exactly when words change.
 * Walks `pose.drawOrder`; slot s shows `pose.slotAttachment[s]` (core resolved it through the skins, §6.14, so
 * `input.skin` is not read). Regions and rigid meshes use `slotWorld` (W2-21); skinned meshes use their influence
 * bones; deform is `pose.deform[attachment]`. Alpha-0 slots are still emitted.
 * Light word = slot colour × attachment colour (straight, rounded per channel); dark word = slot dark RGB, byte 3 = 0.
 * Draws are maximal runs of equal (page, blend); `splitRuns: 'slot'` also writes one run per drawn slot, whose
 * vertices all carry that slot's light word. `pose.flipX` is placement and is never applied here (W2-13).
 * Throws (invariant) on a too-small `out` or a non-skeleton page; a pass that throws bumps both versions (it may
 * have rewritten words), so a consumer never keeps an upload of a half-rewritten `out`.
 */
export function buildRender(out: RenderBuffers, input: { pose: Pose; data: SkeletonData; skin: SkinData; textures: TexturePages; splitRuns?: 'pageBlend' | 'slot' }): void {
  const { pose, data } = input;
  const bySlot = input.splitRuns === 'slot';
  const skeletonPages = input.textures.white;
  reset();
  let complete = false;
  try {
    for (let k = 0; k < pose.drawOrder.length; k++) emit(out, pose, data, pose.drawOrder[k] ?? -1, skeletonPages, bySlot);
    flushDraw(out, st);
    finish(out);
    complete = true;
  } finally {
    if (!complete) {
      out.version += 1;
      out.topologyVersion += 1;
    }
  }
}
